import { supabase } from '../utils/supabase';

/**
 * Service for managing profile views tracking
 */
export class ProfileViewsService {
  /**
   * Track a profile view
   * @param {string} profileId - The ID of the profile being viewed
   * @param {Object} options - Additional tracking options
   * @param {string} options.viewerId - The ID of the user viewing the profile (optional)
   * @param {string} options.ipAddress - IP address of the viewer (optional)
   * @param {string} options.userAgent - Browser user agent (optional)
   * @param {string} options.referrer - Referrer URL (optional)
   * @returns {Promise<Object>} Result of the tracking operation
   */
  static async trackProfileView(profileId, options = {}) {
    try {
      const { viewerId, ipAddress, userAgent, referrer } = options;
      
      // Check if profileId is valid before proceeding
      if (!profileId) {
        console.warn('Invalid profileId provided for profile view');
        return { success: false, error: 'Invalid profile ID' };
      }

      // Get current user (if authenticated)
      const { data: { user } } = await supabase.auth.getUser();
      
      // Don't track self-views
      if (user && user.id === profileId) {
        return { success: true, message: 'Self-view not tracked' };
      }

      // Prepare view data
      const viewData = {
        profile_id: profileId,
        viewer_id: viewerId || user?.id || null,
        ip_address: ipAddress || null,
        user_agent: userAgent || navigator?.userAgent || null,
        referrer: referrer || document?.referrer || null,
        viewed_at: new Date().toISOString()
      };

      // Try inserting the view data
      const { data, error } = await supabase
        .from('profile_views')
        .insert(viewData);

      // Handle insert errors
      if (error) {
        console.error('Error tracking profile view:', error);
        
        // Special handling for auth errors
        if (error.code === '403' || error.message?.includes('JWT') || error.message?.includes('auth')) {
          console.warn('Authentication issue when tracking profile view. Attempting to refresh session...');
          
          try {
            // Try to refresh the session
            const { error: refreshError } = await supabase.auth.refreshSession();
            
            if (!refreshError) {
              // Retry the insert after refresh
              const retryResult = await supabase
                .from('profile_views')
                .insert(viewData);
                
              if (retryResult.error) {
                console.error('Profile view tracking failed after session refresh:', retryResult.error);
              } else {
                console.log('Profile view tracked successfully after session refresh');
                await this.incrementProfileViewCounter(profileId);
                return { success: true, data: retryResult.data };
              }
            }
          } catch (refreshError) {
            console.error('Error refreshing session:', refreshError);
          }
        }
        
        // If we get here, either the refresh failed or we had a different error
        // Fallback to just incrementing the counter
        console.log('Using fallback method to track profile view');
        await this.incrementProfileViewCounter(profileId);
        
        return { 
          success: true, 
          message: 'Used fallback tracking method',
          error: error.message 
        };
      }

      // If we successfully inserted the view, increment the counter as well
      await this.incrementProfileViewCounter(profileId);
      return { success: true, data };
    } catch (error) {
      console.error('Error in trackProfileView:', error);
      
      // Even if the detailed tracking failed, try to increment the counter
      await this.incrementProfileViewCounter(profileId);
      
      return { 
        success: true, 
        message: 'Used fallback tracking method',
        error: error.message 
      };
    }
  }
  
  /**
   * Increment the total profile views counter for a user
   * This is a fallback method that doesn't require authentication
   * and uses a special RLS policy for anonymous updates
   * @param {string} profileId - The user's profile ID
   * @private
   */
  static async incrementProfileViewCounter(profileId) {
    if (!profileId) return;
    
    try {
      // First get the current value
      const { data: profile, error: fetchError } = await supabase
        .from('user_profiles')
        .select('total_profile_views')
        .eq('id', profileId)
        .single();
        
      if (fetchError) {
        console.error('Error fetching profile view count:', fetchError);
        return;
      }
        
      if (profile) {
        const currentViews = profile.total_profile_views || 0;
        
        // Increment the counter
        const { error: updateError } = await supabase
          .from('user_profiles')
          .update({ 
            total_profile_views: currentViews + 1,
            updated_at: new Date().toISOString()
          })
          .eq('id', profileId);
          
        if (updateError) {
          console.error('Error updating profile view counter:', updateError);
          
          // If we get a permission error, try the anonymous function call
          if (updateError.code === '403' || updateError.message?.includes('permission')) {
            console.log('Using function call to increment profile view counter');
            
            // Call the server-side function that's allowed for anonymous users
            // This function could be added to handle anonymous view increments
            try {
              const { error: functionError } = await supabase.rpc(
                'increment_profile_view_count', 
                { profile_id: profileId }
              );
              
              if (functionError) {
                console.error('Function call to increment view count failed:', functionError);
              } else {
                console.log('Successfully incremented view count via function call');
              }
            } catch (functionCallError) {
              console.error('Error calling increment function:', functionCallError);
            }
          }
        } else {
          console.log('Successfully incremented profile view counter');
        }
      } else {
        console.warn(`Profile not found for ID: ${profileId}`);
      }
    } catch (error) {
      console.error('Failed to update profile view counter:', error);
    }
  }

  /**
   * Get profile views for a specific profile
   * @param {string} profileId - The ID of the profile
   * @param {Object} options - Query options
   * @param {number} options.limit - Maximum number of views to return
   * @param {string} options.startDate - Start date for filtering (ISO string)
   * @param {string} options.endDate - End date for filtering (ISO string)
   * @returns {Promise<Object>} Profile views data
   */
  static async getProfileViews(profileId, options = {}) {
    try {
      const { limit = 100, startDate, endDate } = options;

      let query = supabase
        .from('profile_views')
        .select(`
          id,
          viewer_id,
          viewed_at,
          ip_address,
          user_agent,
          referrer,
          viewer:viewer_id(
            id,
            full_name,
            profile_picture
          )
        `)
        .eq('profile_id', profileId)
        .order('viewed_at', { ascending: false });

      if (startDate) {
        query = query.gte('viewed_at', startDate);
      }

      if (endDate) {
        query = query.lte('viewed_at', endDate);
      }

      if (limit) {
        query = query.limit(limit);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching profile views:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error in getProfileViews:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get profile view statistics
   * @param {string} profileId - The ID of the profile
   * @param {number} days - Number of days to look back (default: 30)
   * @returns {Promise<Object>} Profile view statistics
   */
  static async getProfileViewStats(profileId, days = 30) {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const { data: views, error } = await supabase
        .from('profile_views')
        .select('viewed_at, viewer_id')
        .eq('profile_id', profileId)
        .gte('viewed_at', startDate.toISOString());

      if (error) {
        console.error('Error fetching profile view stats:', error);
        return { success: false, error: error.message };
      }

      // Calculate statistics
      const totalViews = views.length;
      const uniqueViewers = new Set(views.filter(v => v.viewer_id).map(v => v.viewer_id)).size;
      const anonymousViews = views.filter(v => !v.viewer_id).length;

      // Group by day for trend analysis
      const viewsByDay = {};
      views.forEach(view => {
        const day = new Date(view.viewed_at).toISOString().split('T')[0];
        viewsByDay[day] = (viewsByDay[day] || 0) + 1;
      });

      // Calculate average views per day
      const avgViewsPerDay = totalViews / days;

      return {
        success: true,
        data: {
          totalViews,
          uniqueViewers,
          anonymousViews,
          avgViewsPerDay: Math.round(avgViewsPerDay * 100) / 100,
          viewsByDay,
          period: days
        }
      };
    } catch (error) {
      console.error('Error in getProfileViewStats:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get recent profile viewers
   * @param {string} profileId - The ID of the profile
   * @param {number} limit - Maximum number of viewers to return
   * @returns {Promise<Object>} Recent viewers data
   */
  static async getRecentViewers(profileId, limit = 10) {
    try {
      const { data, error } = await supabase
        .from('profile_views')
        .select(`
          id,
          viewer_id,
          viewed_at,
          viewer:viewer_id(
            id,
            full_name,
            profile_picture,
            role
          )
        `)
        .eq('profile_id', profileId)
        .not('viewer_id', 'is', null)
        .order('viewed_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching recent viewers:', error);
        return { success: false, error: error.message };
      }

      // Remove duplicate viewers, keeping most recent view
      const uniqueViewers = [];
      const seenViewers = new Set();

      data.forEach(view => {
        if (!seenViewers.has(view.viewer_id)) {
          seenViewers.add(view.viewer_id);
          uniqueViewers.push(view);
        }
      });

      return { success: true, data: uniqueViewers };
    } catch (error) {
      console.error('Error in getRecentViewers:', error);
      return { success: false, error: error.message };
    }
  }
}

export default ProfileViewsService;
